/**
 * ERC-8021 — Transaction Attribution for Ethereum
 *
 * Appends a standardized data suffix to transaction calldata, enabling protocols,
 * chains, and analytics tools to attribute the transaction back to Kojiru.
 *
 * Spec: https://eips.ethereum.org/EIPS/eip-8021
 * Base Builder Codes: https://base.dev
 *
 * Suffix layout (parsed backwards from end of calldata):
 *   [ ercMarker (16 bytes) | schemaId (1 byte) | schemaData (N bytes) | function calldata ]
 *
 * Schema 0 format for `schemaData`:
 *   utf8(code1,code2,...)  +  1-byte total length of the utf8 string
 */

const ERC_MARKER = '80218021802180218021802180218021'; // 16 bytes (no 0x)
const SCHEMA_ID = '00'; // schema 0

/**
 * Build the raw ERC-8021 data suffix for a single builder code.
 *
 * @param {string} code - The registered Builder Code (e.g. "kojiru").
 *                        Only ASCII-compatible characters are safely supported by schema 0.
 * @returns {string} hex string WITHOUT leading 0x (ready to concatenate onto existing calldata)
 */
export function buildAttributionSuffix(code) {
  if (!code || typeof code !== 'string') return '';

  // Encode code as utf-8 bytes
  const bytes = new TextEncoder().encode(code);
  if (bytes.length === 0) return '';
  if (bytes.length > 255) {
    // Schema 0 uses a single byte to describe codes string length
    throw new Error('ERC-8021 schema 0: code string exceeds 255 bytes');
  }

  const codesHex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const lengthHex = bytes.length.toString(16).padStart(2, '0');
  const schemaData = codesHex + lengthHex;

  return schemaData + SCHEMA_ID + ERC_MARKER;
}

/**
 * Resolve the Builder Code from environment config. Returns null if not set
 * (callers should treat that as "attribution disabled" and fall back to normal tx).
 *
 * Configuration lives in REACT_APP_BUILDER_CODE so it can be flipped per-environment
 * without a code change.
 */
export function getBuilderCode() {
  const raw = (process.env.REACT_APP_BUILDER_CODE || '').trim();
  // Allowed chars (from base.dev code registry rules): lowercase alphanumeric + hyphen
  if (!/^[a-z0-9-]{1,32}$/.test(raw)) return null;
  return raw;
}

/**
 * Send a contract transaction with ERC-8021 attribution appended to calldata.
 *
 * Falls back to a standard ethers.js call if no Builder Code is configured, so this
 * is safe to use everywhere without conditional logic in callers.
 *
 * @param {object} contract       - ethers.Contract instance bound to a signer
 * @param {string} methodName     - Solidity function name, e.g. "stake"
 * @param {Array}  args           - Positional args for the function
 * @param {object} [overrides={}] - Optional tx overrides (value, gasLimit, etc.)
 * @returns {Promise<import('ethers').ContractTransactionResponse>}
 */
export async function sendAttributed(contract, methodName, args = [], overrides = {}) {
  const code = getBuilderCode();

  // No code configured — fall through to the standard ethers.js call path.
  if (!code) {
    return contract[methodName](...args, overrides);
  }

  // Build standard calldata via the contract's ABI interface, then append suffix.
  const baseCalldata = contract.interface.encodeFunctionData(methodName, args);
  const suffix = buildAttributionSuffix(code);
  const data = baseCalldata + suffix; // suffix has no leading 0x

  const signer = contract.runner;
  const to = await contract.getAddress();

  return signer.sendTransaction({
    to,
    data,
    ...overrides,
  });
}
